/** Device registration and revocation (desktop-facing). */
import { z } from 'zod';
import { DeviceIdSchema } from '../ids.js';
import { Ed25519JwkSchema, EmptyRequestSchema, NoQuerySchema, SemVerSchema, defineRoute } from './common.js';
import { DeviceSchema } from './entities.js';

export const DeviceRegisterRequestSchema = z
  .object({
    /** Ed25519 public key generated on first run; private key stays in safeStorage. */
    publicKey: Ed25519JwkSchema,
    name: z.string().min(1).max(80),
    platform: z.enum(['darwin-arm64', 'darwin-x64']),
    osVersion: z.string().min(1).max(40),
    appVersion: SemVerSchema,
    /** Re-registration of a known device after key rotation. */
    previousDeviceId: DeviceIdSchema.optional(),
  })
  .strict();
export type DeviceRegisterRequest = z.infer<typeof DeviceRegisterRequestSchema>;

export const DeviceRegisterResponseSchema = z.object({
  device: DeviceSchema,
  /** True when the server sent a device-registration alert to the user. */
  alertSent: z.boolean(),
});
export type DeviceRegisterResponse = z.infer<typeof DeviceRegisterResponseSchema>;

export const DeviceListResponseSchema = z.object({ items: z.array(DeviceSchema) });
export const DeviceParamsSchema = z.object({ deviceId: DeviceIdSchema });

export const deviceRoutes = {
  'devices.register': defineRoute({
    id: 'devices.register',
    method: 'POST',
    path: '/v1/devices',
    auth: 'desktop',
    requestSchema: DeviceRegisterRequestSchema,
    responseSchema: DeviceRegisterResponseSchema,
    description: 'Register this Mac and bind its public signing key to the authenticated user.',
  }),
  'devices.list': defineRoute({
    id: 'devices.list',
    method: 'GET',
    path: '/v1/devices',
    auth: 'desktop',
    requestSchema: NoQuerySchema,
    responseSchema: DeviceListResponseSchema,
    description: "List the caller's registered devices.",
  }),
  'devices.revoke': defineRoute({
    id: 'devices.revoke',
    method: 'POST',
    path: '/v1/devices/:deviceId/revoke',
    auth: 'desktop',
    paramsSchema: DeviceParamsSchema,
    requestSchema: EmptyRequestSchema,
    responseSchema: z.object({ device: DeviceSchema }),
    description: "Revoke one of the caller's own devices (administrators use admin.devices.revoke).",
  }),
} as const;
